import { createSlice } from "@reduxjs/toolkit";
import { status } from "../../_constants/common";
import {
    fetchUnreadCount,
    fetchUserNotifications,
    toggleNotificationRead,
    markAllNotificationsRead,
    markNotificationRead,
} from "./NotificationsThunk";

const notificationSlice = createSlice({
    name: "notifications",
    initialState: {
        list: {
            status: null,
            data: [],
            total: 0,
            page: 1,
            lastPage: 1,
            error: null,
        },
        unread: {
            status: null,
            count: 0,
            error: null,
        },
        actionStatus: {
            status: null,
            error: null,
        },
    },
    reducers: {
        setUnreadCount: (state, action) => {
            // console.log('Redux - Manually setting unread count to:', action.payload);
            state.unread.count = action.payload;
        },
    },
    extraReducers: (builder) => {
        builder
            // FETCH LIST
            .addCase(fetchUserNotifications.pending, (state) => {
                state.list.status = status.IN_PROGRESS;
                state.list.error = null;
            })
            .addCase(fetchUserNotifications.fulfilled, (state, { payload }) => {
                state.list.status = status.SUCCESS;

                state.list.data = Array.isArray(payload?.data) ? payload.data : [];
                state.list.total = payload?.total ?? 0;
                state.list.page = payload?.page ?? 1;
                state.list.lastPage = payload?.lastPage ?? 1;
            })
            .addCase(fetchUserNotifications.rejected, (state, action) => {
                state.list.status = status.FAILURE;
                state.list.error = action.payload || action.error?.message;
            })

            // UNREAD COUNT
            .addCase(fetchUnreadCount.pending, (state) => {
                state.unread.status = status.IN_PROGRESS;
                state.unread.error = null;
            })
            .addCase(fetchUnreadCount.fulfilled, (state, { payload }) => {
                // console.log('Redux - fetchUnreadCount fulfilled, payload:', payload);
                state.unread.status = status.SUCCESS;
                state.unread.count = payload?.unreadCount ?? 0;
                // console.log('Redux - Updated unread count to:', state.unread.count);
            })
            .addCase(fetchUnreadCount.rejected, (state, action) => {
                // console.log('Redux - fetchUnreadCount rejected:', action.payload);
                state.unread.status = status.FAILURE;
                state.unread.error = action.payload || action.error?.message;
            })

            // MARK READ (single)
            .addCase(markNotificationRead.pending, (state) => {
                state.actionStatus.status = status.IN_PROGRESS;
                state.actionStatus.error = null;
            })
            .addCase(markNotificationRead.fulfilled, (state, { payload }) => {
                state.actionStatus.status = status.SUCCESS;
                const id = payload.id;
                const notification = state.list.data.find((n) => n.id === id);
                // Decrement unread count if notification was unread
                if (notification && !notification.isRead) {
                    state.unread.count = Math.max(0, state.unread.count - 1);
                }
                state.list.data = state.list.data.map((n) =>
                    n.id === id ? { ...n, isRead: true } : n
                );
            })
            .addCase(markNotificationRead.rejected, (state, action) => {
                state.actionStatus.status = status.FAILURE;
                state.actionStatus.error = action.payload || action.error?.message;
            })

            // TOGGLE READ
            .addCase(toggleNotificationRead.pending, (state) => {
                state.actionStatus.status = status.IN_PROGRESS;
                state.actionStatus.error = null;
            })
            .addCase(toggleNotificationRead.fulfilled, (state, { payload }) => {
                state.actionStatus.status = status.SUCCESS;
                const id = payload.id;
                const notification = state.list.data.find((n) => n.id === id);
                // Update unread count based on toggle
                if (notification) {
                    if (notification.isRead) {
                        // Was read, now unread - increment
                        state.unread.count = state.unread.count + 1;
                    } else {
                        // Was unread, now read - decrement
                        state.unread.count = Math.max(0, state.unread.count - 1);
                    }
                }
                state.list.data = state.list.data.map((n) =>
                    n.id === id ? { ...n, isRead: !n.isRead } : n
                );
            })
            .addCase(toggleNotificationRead.rejected, (state, action) => {
                state.actionStatus.status = status.FAILURE;
                state.actionStatus.error = action.payload || action.error?.message;
            })

            // MARK ALL READ
            .addCase(markAllNotificationsRead.pending, (state) => {
                state.actionStatus.status = status.IN_PROGRESS;
                state.actionStatus.error = null;
            })
            .addCase(markAllNotificationsRead.fulfilled, (state) => {
                state.actionStatus.status = status.SUCCESS;
                state.list.data = state.list.data.map((n) => ({ ...n, isRead: true }));
                state.unread.count = 0;
            })
            .addCase(markAllNotificationsRead.rejected, (state, action) => {
                state.actionStatus.status = status.FAILURE;
                state.actionStatus.error = action.payload || action.error?.message;
            })


    },
});

export const { setUnreadCount } = notificationSlice.actions;
export default notificationSlice.reducer;
